import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import { AppModal } from "../../shared/components/AppModal";
import { useAppConfirm } from "../../shared/components/AppConfirmDialog";
import { PagedListControls } from "../../shared/components/PagedListControls";
import { PageLoading } from "../../shared/components/PageLoading";
import { listParams, SortableHeaders, useUrlSortedRows } from "../../shared/components/SortableTableHeader";
import { getPreferredPageSize, rememberPageSize } from "../../shared/pagination/pageSizePreference";
import { getMaterialDeletionAvailability, getMaterials, materialCommand, peekMaterials } from "./materialsApi";
import type {
  MaterialSnapshot,
  WarehouseItem,
  WarehouseLocationItem,
} from "./types";
import { useIntegrationProtection } from "../../shared/integrations/useIntegrationProtection";
import { IntegrationManagedCheck, IntegrationManagedField } from "../../shared/integrations/IntegrationManagedField";

type Editor =
  | { kind: "warehouse"; item: WarehouseItem | null }
  | { kind: "locations"; warehouse: WarehouseItem }
  | {
      kind: "location";
      item: WarehouseLocationItem | null;
      warehouse: WarehouseItem;
    };

export function WarehousesPage() {
  const { user } = useAuth();
  const { t, formatNumber, locale } = useLocale();
  const { confirm, confirmationDialog } = useAppConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? 1) || 1);
  const pageSize = getPreferredPageSize(searchParams);
  const appliedQuery = searchParams.get("q") ?? "";
  const appliedStatus = searchParams.get("status") ?? "";
  const [query, setQuery] = useState(appliedQuery);
  const [status, setStatus] = useState(appliedStatus);
  useEffect(() => { setQuery(appliedQuery); setStatus(appliedStatus); },
    [appliedQuery, appliedStatus]);
  const canEdit = user?.permissions.materials === "Write";
  const warehouseProtection = useIntegrationProtection("Warehouse");
  const locationProtection = useIntegrationProtection("WarehouseLocation");
  const materialOptions={section:"warehouses", query:appliedQuery,
    status:appliedStatus, page, pageSize, cacheScope:user?.activeCompanyId};
  const [data, setData] = useState<MaterialSnapshot | null>(() => peekMaterials(materialOptions));
  const [editor, setEditor] = useState<Editor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    try {
      setData(await getMaterials(materialOptions));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "materials.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, [user?.activeCompanyId, appliedQuery, appliedStatus, page, pageSize]);
  useEffect(() => {
    if (!data) return;
    setError(null);
    if (searchParams.get("new") === "1" && canEdit && warehouseProtection.canCreate) { setEditor({ kind: "warehouse", item: null }); return; }
    const editId = searchParams.get("edit");
    const locationsId = searchParams.get("warehouse");
    const warehouse = data.warehouses.find(
      (item) => item.id === (editId ?? locationsId),
    );
    if (warehouse && editId) setEditor({ kind: "warehouse", item: warehouse });
    const locationId = searchParams.get("location");
    if (warehouse && locationsId && locationId)
      setEditor({ kind: "location", warehouse, item: data.warehouseLocations.find(item => item.id === locationId) ?? null });
    else if (warehouse && locationsId && warehouse.managesLocations)
      setEditor({ kind: "locations", warehouse });
    else if (!editId && !locationsId) setEditor(null);
  }, [canEdit, data, searchParams, warehouseProtection.canCreate]);
  const closeEditor = () => {
    setError(null); setEditor(null);
    const next = new URLSearchParams(searchParams);
    next.delete("edit"); next.delete("new"); next.delete("warehouse"); next.delete("location");
    setSearchParams(next, { replace: true });
  };
  const save = async (path: string, method: "POST" | "PUT", body: unknown) => {
    try {
      setMessage(await materialCommand(path, method, body));
      closeEditor();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "materials.actionError");
    }
  };
  const remove = async (path: string, after: () => void = closeEditor) => {
    setError(null);
    try { const availability = await getMaterialDeletionAvailability(path);
      if (!availability.allowed) { setError(availability.code); return; }
      if (!await confirm(t("deletion.confirm"), undefined, true)) return;
      setMessage(await materialCommand(path, "DELETE")); after(); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "deletion.unavailable"); }
  };
  const tableSort=useUrlSortedRows(data?.warehouses??[],{code:x=>x.code,name:x=>x.name,description:x=>x.description,
    locations:x=>data?.warehouseLocations.filter(location=>location.warehouseId===x.id).length,
    negative:x=>x.preventNegativeStock,status:x=>x.isActive},"code","asc",locale);
  if (!data) return <PageLoading label={t("materials.loading")}
    error={error ? t(error as Parameters<typeof t>[0]) : null}/>;
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t("materials.eyebrow")}</span>
          <h1>{t("materials.warehouses")}</h1>
          <p>
            {data.companyName} · {t("materials.warehousesDescription")}
          </p>
        </div>
      </header>
      {error && !editor && (
        <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>
      )}
      {message && (
        <p className="form-success">{t(message as Parameters<typeof t>[0])}</p>
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("materials.warehouses")}</h2>
          {canEdit && warehouseProtection.canCreate && (
            <button className="primary-button"
              onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), new: "1" })}>
              + {t("materials.createWarehouse")}
            </button>
          )}
        </div>
        <PagedListControls applyLabel={t("recordList.applyFilters")} clearLabel={t("recordList.clearFilters")}
          rowsPerPageLabel={t("recordList.rowsPerPage")} previousLabel={t("recordList.previousPage")}
          nextLabel={t("recordList.nextPage")} pageLabel={t("recordList.pageOf")}
          totalLabel={t("recordList.totalRecords")} page={page} pageSize={pageSize}
          totalCount={data.totalCount} formatNumber={formatNumber}
          filters={<><label><span>{t("nav.search")}</span><input value={query}
            placeholder={t("recordList.search")} onChange={event=>setQuery(event.target.value)}/></label>
            <label><span>{t("administration.status")}</span><select value={status}
              onChange={event=>setStatus(event.target.value)}><option value="">{t("recordList.allStatuses")}</option>
              <option value="active">{t("administration.active")}</option>
              <option value="inactive">{t("administration.inactive")}</option></select></label></>}
          onApply={()=>setSearchParams(listParams(searchParams,{q:query.trim()||undefined,status:status||undefined,page:"1",pageSize:String(pageSize)}))}
          onClear={()=>{setQuery("");setStatus("");setSearchParams(listParams(searchParams,{page:"1",pageSize:String(pageSize)}))}}
          onPageChange={next=>setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:String(next),pageSize:String(pageSize)}))}
          onPageSizeChange={next=>{rememberPageSize(next);setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:"1",pageSize:String(next)}))}}/>
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <SortableHeaders sort={tableSort} columns={[["code",t("aplPanel.code")],["name",t("aplPanel.name")],["description",t("materials.description")],["locations",t("materials.locationManagement")],["negative",t("materials.preventNegativeStock")],["status",t("administration.status")]]}/>
              </tr>
            </thead>
            <tbody>
              {data.warehouses.length === 0 && (
                <tr><td className="empty-state" colSpan={6}>{t("administration.empty")}</td></tr>
              )}
              {tableSort.sortedRows.map((w) => {
                const count = data.warehouseLocations.filter(
                  (x) => x.warehouseId === w.id,
                ).length;
                return (
                  <tr key={w.id}>
                    <td>
                      <Link className="record-code-link" to={`?${new URLSearchParams({...Object.fromEntries(searchParams),edit:w.id}).toString()}`}
                        state={{recordIds:tableSort.sortedRows.map(value=>value.id)}}>{w.code}</Link>
                    </td>
                    <td>{w.name}</td>
                    <td>{w.description || "—"}</td>
                    <td>
                      {t(
                        w.managesLocations
                          ? "administration.yes"
                          : "administration.no",
                      )}
                      {w.managesLocations && (
                        <small>
                          {t("materials.locationCount").replace(
                            "{count}",
                            String(count),
                          )}
                        </small>
                      )}
                    </td>
                    <td>
                      {t(
                        w.preventNegativeStock
                          ? "administration.yes"
                          : "administration.no",
                      )}
                    </td>
                    <td>
                      {t(
                        w.isActive
                          ? "administration.active"
                          : "administration.inactive",
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      {editor?.kind === "warehouse" && (
        <AppModal
          title={t(
            editor.item
              ? "materials.editWarehouse"
              : "materials.createWarehouse",
          )}
          closeLabel={t("nav.closeMenu")}
          onClose={closeEditor}
          saveForm={canEdit}
          onDelete={editor.item && canEdit && warehouseProtection.canDelete ? () => remove(`/warehouses/${editor.item!.id}`) : null}
          feedback={error ? t(error as Parameters<typeof t>[0]) : null}
        >
          <fieldset className={!canEdit ? "readonly-detail" : undefined} disabled={!canEdit}><WarehouseForm key={editor.item?.id ?? "new"}
            item={editor.item}
            save={(body) =>
              save(
                `/warehouses${editor.item ? `/${editor.item.id}` : ""}`,
                editor.item ? "PUT" : "POST",
                body,
              )
            }
          /></fieldset>
          {editor.item?.managesLocations && <div className="record-related-actions">
            <Link className="secondary-button" to={`?warehouse=${editor.item.id}`}>
              {t("materials.manageLocations")}
            </Link>
          </div>}
        </AppModal>
      )}
      {editor?.kind === "locations" && (
        <LocationsModal
          warehouse={editor.warehouse}
          locations={data.warehouseLocations}
          canEdit={canEdit && locationProtection.canCreate}
          close={closeEditor}
          edit={(item) =>
            setSearchParams({ warehouse: editor.warehouse.id, ...(item ? { location: item.id } : {}) })
          }
        />
      )}{" "}
      {editor?.kind === "location" && (
        <AppModal
          title={t(
            editor.item ? "materials.editLocation" : "materials.createLocation",
          )}
          closeLabel={t("nav.closeMenu")}
          onClose={() => setSearchParams({ warehouse: editor.warehouse.id })}
          saveForm={canEdit}
          onDelete={editor.item && canEdit && locationProtection.canDelete ? () => remove(`/warehouse-locations/${editor.item!.id}`, () => setSearchParams({warehouse:editor.warehouse.id})) : null}
          feedback={error ? t(error as Parameters<typeof t>[0]) : null}
        >
          <fieldset className={!canEdit ? "readonly-detail" : undefined} disabled={!canEdit}><LocationForm key={editor.item?.id ?? "new"}
            item={editor.item}
            warehouseId={editor.warehouse.id}
            locations={data.warehouseLocations}
            save={(body) =>
              save(
                `/warehouse-locations${editor.item ? `/${editor.item.id}` : ""}`,
                editor.item ? "PUT" : "POST",
                body,
              )
            }
          /></fieldset>
        </AppModal>
      )}
      {confirmationDialog}
    </main>
  );
}

function LocationsModal({
  warehouse,
  locations,
  canEdit,
  close,
  edit,
}: {
  warehouse: WarehouseItem;
  locations: WarehouseLocationItem[];
  canEdit: boolean;
  close: () => void;
  edit: (item: WarehouseLocationItem | null) => void;
}) {
  const { t } = useLocale();
  const own = locations.filter((x) => x.warehouseId === warehouse.id);
  return (
    <AppModal
      wide
      title={`${t("materials.locations")} · ${warehouse.code}`}
      closeLabel={t("nav.closeMenu")}
      onClose={close}
    >
      <div className="location-manager">
        <div className="section-heading">
          <div>
            <h3>{warehouse.name}</h3>
            <p>{t("materials.locationsDescription")}</p>
          </div>
          {canEdit && (
            <button className="primary-button" onClick={() => edit(null)}>
              + {t("materials.createLocation")}
            </button>
          )}
        </div>
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <th>{t("aplPanel.code")}</th>
                <th>{t("aplPanel.name")}</th>
                <th>{t("materials.parent")}</th>
                <th>{t("administration.status")}</th>
              </tr>
            </thead>
            <tbody>
              {own.map((l) => (
                <tr key={l.id}>
                  <td><button className="record-code-link button-link" type="button" onClick={() => edit(l)}>{l.code}</button></td>
                  <td>{l.name}</td>
                  <td>{own.find((x) => x.id === l.parentId)?.name ?? "—"}</td>
                  <td>
                    {t(
                      l.isActive
                        ? "administration.active"
                        : "administration.inactive",
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!own.length && (
          <p className="empty-state">{t("materials.noLocations")}</p>
        )}
      </div>
    </AppModal>
  );
}

function WarehouseForm({
  item,
  save,
}: {
  item: WarehouseItem | null;
  save: (body: unknown) => void;
}) {
  const { t } = useLocale();
  const protection = useIntegrationProtection("Warehouse");
  const managed = protection.isProtected;
  const [f, setF] = useState({
    code: item?.code ?? "",
    name: item?.name ?? "",
    description: item?.description ?? "",
    preventNegativeStock: item?.preventNegativeStock ?? true,
    managesLocations: item?.managesLocations ?? false,
    isActive: item?.isActive ?? true,
  });
  return (
    <form
      className="administration-form modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        save({ ...f, code: item ? null : f.code });
      }}
    >
      <div className="compact-form-grid">
        <IntegrationManagedField label={t("aplPanel.code")} managed={managed("code")}>
          <input
            required
            disabled={!!item}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
          />
        </IntegrationManagedField>
        <IntegrationManagedField label={t("aplPanel.name")} managed={managed("name")}>
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </IntegrationManagedField>
        <IntegrationManagedField label={t("materials.description")} managed={managed("description")}>
          <textarea
            maxLength={500}
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </IntegrationManagedField>
      </div>
      <ManagedCheck
        label={t("materials.locationManagement")}
        value={f.managesLocations}
        set={(value) => setF({ ...f, managesLocations: value })}
        managed={managed("managesLocations")}
      />
      <ManagedCheck
        label={t("materials.preventNegativeStock")}
        value={f.preventNegativeStock}
        set={(value) => setF({ ...f, preventNegativeStock: value })}
        managed={managed("preventNegativeStock")}
      />
      <ManagedCheck
        label={t("administration.active")}
        value={f.isActive}
        set={(value) => setF({ ...f, isActive: value })}
        managed={managed("isActive")}
      />
      <Actions />
    </form>
  );
}
function LocationForm({
  item,
  warehouseId,
  locations,
  save,
}: {
  item: WarehouseLocationItem | null;
  warehouseId: string;
  locations: WarehouseLocationItem[];
  save: (body: unknown) => void;
}) {
  const { t } = useLocale();
  const protection = useIntegrationProtection("WarehouseLocation");
  const managed = protection.isProtected;
  const [f, setF] = useState({
    code: item?.code ?? "",
    name: item?.name ?? "",
    description: item?.description ?? "",
    parentId: item?.parentId ?? "",
    isActive: item?.isActive ?? true,
  });
  return (
    <form
      className="administration-form modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        save({
          ...f,
          warehouseId,
          code: item ? null : f.code,
          parentId: f.parentId || null,
        });
      }}
    >
      <div className="compact-form-grid">
        <IntegrationManagedField label={t("aplPanel.code")} managed={managed("code")}>
          <input
            required
            disabled={!!item}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
          />
        </IntegrationManagedField>
        <IntegrationManagedField label={t("aplPanel.name")} managed={managed("name")}>
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </IntegrationManagedField>
        <IntegrationManagedField label={t("materials.parent")} managed={managed("parentCode")}>
          <select
            value={f.parentId}
            onChange={(e) => setF({ ...f, parentId: e.target.value })}
          >
            <option value="">—</option>
            {locations
              .filter((x) => x.warehouseId === warehouseId && x.id !== item?.id)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.name}
                </option>
              ))}
          </select>
        </IntegrationManagedField>
        <IntegrationManagedField label={t("materials.description")} managed={managed("description")}>
          <textarea
            maxLength={500}
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </IntegrationManagedField>
      </div>
      <ManagedCheck
        label={t("administration.active")}
        value={f.isActive}
        set={(value) => setF({ ...f, isActive: value })}
        managed={managed("isActive")}
      />
      <Actions />
    </form>
  );
}
function ManagedCheck({ label, value, set, managed }: { label: string; value: boolean; set: (value: boolean) => void; managed: boolean }) {
  return <IntegrationManagedCheck label={label} managed={managed}><input type="checkbox" checked={value} onChange={e => set(e.target.checked)} /></IntegrationManagedCheck>
}
function Actions() {
  const { t } = useLocale();
  return (
    <div className="form-actions">
      <button className="primary-button" type="submit">
        {t("administration.save")}
      </button>
    </div>
  );
}
